import React, { useEffect } from 'react';
import styles from './CreateProductStageNavigationBar.module.scss';
import { ReactComponent as Check } from 'assets/icons/ticket_closed.svg';
import { ReactComponent as Truck } from 'assets/icons/transportation.svg';
import { ReactComponent as RawMaterial } from 'assets/icons/raw_material.svg';
import { ReactComponent as ProcessingFacility } from 'assets/icons/processing_factor.svg';
import { ReactComponent as ProductDistribution } from 'assets/icons/distribution.svg';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import AlertAnimation from '../AlertAnimation';
import { isActiveTab } from '../DataMapDrawer/DataMapDrawerHelper';
import { DataMappingProps } from 'enums/CreateProductStageNavigationBarEnums';
import ProductCreationService from 'services/ProductsService/productCreation.service';

interface NavigationBarProps {
  productId: number | string | undefined;
  stage: number | undefined;
  toggleStatus?: boolean;
  stage1Total?: number | string | null;
  stage2Total?: number | string | null;
  stage3Total?: number | string | null;
  stage4Total?: number | string | null;
  isDisabled?: boolean | null;
}

const CreateProductStageNavigationBar: React.FC<NavigationBarProps> = ({
  productId,
  stage,
  toggleStatus,
  stage1Total,
  stage2Total,
  stage3Total,
  stage4Total,
  isDisabled,
}: NavigationBarProps) => {
  const location = useLocation();
  const isRawMaterial = isActiveTab(DataMappingProps.RAW_MATERIAL, location);
  const isTransportation = isActiveTab(
    DataMappingProps.TRANSPORTATION,
    location
  );

  const isProcessingFacility = isActiveTab(
    DataMappingProps.PROCESSING_FACILITY,
    location
  );
  const isProductDistribution = isActiveTab(
    DataMappingProps.PRODUCT_DISTRIBUTION,
    location
  );
  const isProductOverview = isActiveTab(
    DataMappingProps.PRODUCT_OVERVIEW,
    location
  );
  const { fetchSingleProduct } = ProductCreationService();
  useEffect(() => {
    fetchSingleProduct(Number(productId));
  }, []);

  const pathname = location.pathname;
  const isProductOverviewPage = pathname.startsWith('/product-overview');

  return (
    <div
      className={clsx({
        [styles.stageNavigation]: true,
        [styles.marginLeft0]: isProductOverviewPage,
      })}>
      <div className={styles.test}>
        <div
          className={clsx({
            [styles.navigationItem]: true,
            [styles.roundedBorderLeft]: true,
            [styles.active]: isRawMaterial,
            [styles.complete]:
              !isDisabled && !isRawMaterial && stage && stage > 0,
            [styles.inactive]: !isRawMaterial && stage && stage < 1,
            [styles.disabledState]: isDisabled && !isRawMaterial,
            [styles.overview]: isProductOverview,
          })}>
          <div className={styles.textContainer}>
            <div className={styles.labelIconContainer}>
              <div className={styles.iconContainer}>
                {isRawMaterial ? <RawMaterial /> : <Check />}
              </div>
              <div>
                <p>{DataMappingProps.RAW_MATERIAL_HEADING}</p>
              </div>
            </div>

            {!isRawMaterial && !isDisabled && (
              <div className={styles.labelTextContainer}>
                {stage1Total !== 0 && !Number.isNaN(stage1Total) ? (
                  <h3>
                    {Number(stage1Total).toFixed(4)} {DataMappingProps.CO}
                    <sub>{DataMappingProps.SUB_2}</sub>e
                  </h3>
                ) : (
                  ''
                )}
              </div>
            )}
          </div>
        </div>
        {isRawMaterial && (
          <div className={styles.rawMaterialAlert}>
            <AlertAnimation />
          </div>
        )}
      </div>

      <div className={styles.test}>
        <div
          className={clsx({
            [styles.navigationItem]: true,
            [styles.active]: isTransportation,
            [styles.inactive]: !isTransportation && stage && stage < 2,
            [styles.complete]:
              !isDisabled && !isTransportation && stage && stage > 1,
            [styles.disabledState]: isDisabled && !isTransportation,
            [styles.overview]: isProductOverview,
          })}>
          <div className={styles.textContainer}>
            <div className={styles.labelIconContainer}>
              <div className={styles.iconContainer}>
                {isTransportation ? (
                  <Truck />
                ) : stage && stage >= 2 ? (
                  <Check />
                ) : stage && stage < 3 ? (
                  <h3>{DataMappingProps.SECOND_ITEM}</h3>
                ) : (
                  <h3>{DataMappingProps.SECOND_ITEM}</h3>
                )}
              </div>
              <p>{DataMappingProps.TRANSPORTATION_HEADING}</p>
            </div>

            {!isTransportation && !isDisabled && (
              <div className={styles.labelTextContainer}>
                {stage2Total !== 0 && !Number.isNaN(stage2Total) ? (
                  <h3>
                    {Number(stage2Total).toFixed(4)} {DataMappingProps.CO}
                    <sub>{DataMappingProps.SUB_2}</sub>e
                  </h3>
                ) : (
                  ''
                )}
              </div>
            )}
          </div>
        </div>
        {isTransportation && !toggleStatus && (
          <div className={styles.transportationAlert}>
            <AlertAnimation />
          </div>
        )}
      </div>

      <div className={styles.test}>
        <div
          className={clsx({
            [styles.navigationItem]: true,
            [styles.active]: isProcessingFacility,
            [styles.inactive]: !isProcessingFacility && stage && stage < 3,
            [styles.complete]:
              !isDisabled && !isProcessingFacility && stage && stage >= 3,
            [styles.disabledState]: isDisabled && !isProcessingFacility,
            [styles.overview]: isProductOverview,
          })}>
          <div className={styles.textContainer}>
            <div className={styles.labelIconContainer}>
              <div className={styles.iconContainer}>
                {isProcessingFacility ? (
                  <ProcessingFacility />
                ) : stage && Number(stage) >= 3 ? (
                  <Check />
                ) : stage && Number(stage) < 3 ? (
                  <h3>{DataMappingProps.THIRD_ITEM}</h3>
                ) : (
                  <h3>{DataMappingProps.THIRD_ITEM}</h3>
                )}
              </div>
              <p>{DataMappingProps.PROCESSING_FACILITY_HEADING}</p>
            </div>

            {!isDisabled && !isProcessingFacility && (
              <div className={styles.labelTextContainer}>
                {stage3Total !== 0 && !Number.isNaN(stage3Total) ? (
                  <h3>
                    {Number(stage3Total).toFixed(4)} {DataMappingProps.CO}
                    <sub>{DataMappingProps.SUB_2}</sub>e
                  </h3>
                ) : (
                  ''
                )}
              </div>
            )}
          </div>
        </div>
        {isProcessingFacility && !toggleStatus && (
          <div className={styles.processingFacilityAlert}>
            <AlertAnimation />
          </div>
        )}
      </div>

      <div className={styles.test}>
        <div
          className={clsx({
            [styles.navigationItem]: true,
            [styles.roundedBorderRight]: true,
            [styles.active]: isProductDistribution,
            [styles.inactive]: !isProductDistribution && stage && stage < 4,
            [styles.activeProductOverview]: isProductOverview,
            [styles.complete]:
              !isDisabled && !isProductDistribution && stage && stage === 4,
            [styles.disabledState]: isDisabled && !ProductDistribution,
            [styles.overview]: isProductOverview,
            [styles.fontBold]: isProductOverview,
          })}>
          <div className={styles.textContainer}>
            <div className={styles.labelIconContainer}>
              <div className={styles.iconContainer}>
                {isProductDistribution ? (
                  <ProductDistribution />
                ) : stage && Number(stage) === 4 ? (
                  <Check />
                ) : stage && Number(stage) < 4 ? (
                  <h3 className={
                    clsx({
                      [styles.fontBold]: isProductOverview
                    })
                  }>{DataMappingProps.FOURTH_ITEM}</h3>
                ) : (
                  <h3 className={
                    clsx({
                      [styles.fontBold]: isProductOverview
                    })
                  }>{DataMappingProps.FOURTH_ITEM}</h3>
                )}
              </div>
              <p>{DataMappingProps.PRODUCT_DISTRIBUTION_HEADING}</p>
            </div>

            {!isProductDistribution && !isDisabled && (
              <div className={styles.labelTextContainer}>
                {stage4Total &&
                stage4Total !== 0 &&
                !Number.isNaN(stage4Total) ? (
                  <h3>
                    {Number(stage4Total).toFixed(4)} {DataMappingProps.CO}
                    <sub>{DataMappingProps.SUB_2}</sub>e
                  </h3>
                ) : (
                  ''
                )}
              </div>
            )}
          </div>
        </div>
        {isProductDistribution && !toggleStatus && (
          <div className={styles.productDistributionAlert}>
            <AlertAnimation />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProductStageNavigationBar;
